import type { ComponentType } from 'react'
import { createContext, useContext } from 'react'

import type { ReportField } from '@/lib/cube-report'
import type { WidgetStatus } from '@/features/report/widgets/types'
import type { ReportRow } from '@/services/cube/report'

/*
 * Belge düğümlerinin (value/change/condition/repeat ve widget'lar) tek veri girişi.
 * Şablon önizlemesi CANLI kaynağı (`live-source.tsx`) takar; dönem kopyası (D7) ve baskı (D8)
 * aynı arayüzle app_db'deki DONMUŞ veriyi verir. Düğümler kaynağın canlı mı donmuş mu olduğunu bilmez.
 */

/** Widget düğüm adları (`report-document.ts`); öznitelikleri şema JSON'undaki gibidir. */
export type WidgetNodeName = 'reportKpi' | 'reportTable' | 'reportList' | 'reportChart'

/** Adlandırılmış sorgunun sonucu: satırlar `row['View.ölçü']` biçiminde düz anahtarlıdır. */
export interface NamedResult {
  view: string
  fields: ReadonlyMap<string, ReportField>
  rows: ReportRow[] | undefined
  /** `compare: "previous"` sorgusunda önceki dönem satırları. */
  previousRows?: ReportRow[] | undefined
  /** `forbidden`: view token izninde yok; `invalid`: sorgu tanımı geçersiz (`status.error`). */
  state: 'loading' | 'ready' | 'error' | 'forbidden'
  status: WidgetStatus
}

export interface DocumentDataSource {
  /** `period.label`: seçili dönemin Türkçe adı; dönem yoksa tanımsız. */
  periodLabel: string | undefined
  /** `filters.<üye>`: seçili değerler virgülle, seçim yoksa "Tümü". */
  filterText: (member: string) => string
  /** Hook gibi çağrılır (`useNamedQuery`). Ad tanımsızsa ya da sorgu yoksa `undefined`. */
  useNamedQuery: (name: string | undefined) => NamedResult | undefined
  /** `reportKpi/Table/List/Chart` düğümünün çizimi. `attrs` düğümün özniteliği. */
  Widget: ComponentType<{ type: WidgetNodeName; attrs: Record<string, unknown> }>
}

const EmptyWidget: DocumentDataSource['Widget'] = () => null

/** Sağlayıcısız render (ör. salt şema testleri): hiçbir veri yok, düğümler "—" gösterir. */
export const emptyDataSource: DocumentDataSource = {
  periodLabel: undefined,
  filterText: () => '',
  useNamedQuery: () => undefined,
  Widget: EmptyWidget,
}

const DocumentDataSourceContext = createContext<DocumentDataSource>(emptyDataSource)

export const DocumentDataSourceProvider = DocumentDataSourceContext.Provider

export const useDocumentDataSource = () => useContext(DocumentDataSourceContext)

export function useNamedQuery(name: string | undefined): NamedResult | undefined {
  return useDocumentDataSource().useNamedQuery(name)
}
